/**
 * ⌘K, and the one place that listens for it.
 *
 * Mounted by `AppShell` so the shortcut works in every mode. The corpus is read
 * at open time rather than subscribed to: a palette is on screen for a couple
 * of seconds, and a live subscription to two stores would re-rank the list
 * under the reader's cursor every time a teammate edited anything.
 *
 * The listener is `keydown` on `window` in the capture phase, so it fires
 * before an editor swallows the key -- the Lexical surface below binds ⌘K for
 * its own link command, and a bubble-phase listener here would never see it in
 * the one mode where a reader is most likely to want to jump somewhere else.
 *
 * Reading at open time is what makes authorization this component's problem.
 * The entries are a *copy*: nothing downstream re-reads the stores, so clearing
 * a store does not clear the palette, and the document half is read from
 * scope-keyed atoms that deliberately outlive the session that filled them. A
 * palette that stayed reachable after a team session failed would hand a
 * removed member their old teammates' document titles off the failure screen.
 * Hence `teamSession`, and hence the two effects below that drop the copy.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TeamSession } from '../auth/teamJwt';
import { QuickOpenPalette } from './QuickOpenPalette';
import {
  hasQuickOpenProviders,
  onQuickOpenProvidersChanged,
  quickOpenProviderIds,
  readDocumentEntries,
  readRegisteredEntries,
  type QuickOpenTarget,
} from './quickOpenSources';

interface QuickOpenState {
  open: () => void;
}

const QuickOpenContext = createContext<QuickOpenState>({ open: () => undefined });

/** For a trigger that wants to open the palette by pointer as well as by key. */
export function useQuickOpen(): QuickOpenState {
  return useContext(QuickOpenContext);
}

export interface QuickOpenProviderProps {
  /**
   * The caller's *team* session, or null when there is not one -- the session
   * is still opening, or it failed, or this member was removed from the org.
   * Null makes the palette inert: no shortcut, no corpus, nothing rendered.
   *
   * It is the team session specifically, never the personal one. The personal
   * JWT authorizes personal/mobile sync and nothing this palette can reach; a
   * gate that accepted it would be open to a signed-in user with no team.
   *
   * `children` is still rendered when this is null, and this component is
   * mounted on every branch of the org route on purpose. Making the *element*
   * conditional would change the tree shape at this position and remount the
   * whole shell the moment a team session resolved.
   */
  teamSession: TeamSession | null;
  /**
   * The Shared Docs scope for the active project, or null when no project has
   * resolved. `webCollabScopeKey(orgId, projectId)`; the palette reads the
   * bundle's index atoms under it.
   */
  docsScopeKey: string | null;
  /** Route for a document id in that scope. */
  documentPath: (documentId: string) => string;
  navigate: (path: string) => void;
  children: ReactNode;
}

export function QuickOpenProvider({
  teamSession,
  docsScopeKey,
  documentPath,
  navigate,
  children,
}: QuickOpenProviderProps) {
  const [entries, setEntries] = useState<QuickOpenTarget[] | null>(null);
  /** Which corpora the entries on screen were copied from. See `close`. */
  const openedProviderIds = useRef<readonly string[]>([]);

  const close = useCallback(() => {
    openedProviderIds.current = [];
    setEntries(null);
  }, []);

  const authorized = teamSession !== null;
  const open = useCallback(() => {
    if (!authorized) return;
    const documents = docsScopeKey ? readDocumentEntries(docsScopeKey, documentPath) : [];
    openedProviderIds.current = quickOpenProviderIds();
    setEntries([...documents, ...readRegisteredEntries()]);
  }, [authorized, docsScopeKey, documentPath]);

  useEffect(() => {
    if (!authorized) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // `metaKey` on macOS, `ctrlKey` elsewhere -- and never both, so a
      // Ctrl+Cmd chord does not open it.
      const accel = event.metaKey !== event.ctrlKey;
      if (!accel || event.altKey || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      event.stopPropagation();
      open();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [authorized, open]);

  /*
   * Everything that changes *whose* data the palette may show: the member and
   * org the team session names, whether there is a session at all, and which
   * project scope the documents come from. An org switch, a session failure and
   * a removal all land here, and any of them must take an open palette down
   * with them rather than leave a copied corpus on screen.
   */
  const authorizationKey = teamSession
    ? `${teamSession.orgId}:${teamSession.memberId}:${docsScopeKey ?? ''}`
    : null;
  useEffect(() => close(), [authorizationKey, close]);

  // A corpus withdrawn while the palette is up. The tracker room unregisters
  // when its access gate replaces it, which is the revocation case; the copy
  // taken at open time is the only thing still holding those rows.
  useEffect(() => onQuickOpenProvidersChanged(() => {
    const live = new Set(quickOpenProviderIds());
    if (openedProviderIds.current.some((id) => !live.has(id))) close();
  }), [close]);

  const value = useMemo<QuickOpenState>(() => ({ open }), [open]);

  return (
    <QuickOpenContext.Provider value={value}>
      {children}
      {authorized && (
        <QuickOpenPalette
          open={entries !== null}
          entries={entries ?? []}
          onClose={close}
          onSelect={(target) => navigate(target.path)}
          // The scope has no index at all, which after a cold tracker link is the
          // normal case rather than an error. Say so instead of showing an empty
          // half the reader cannot account for.
          documentsUnavailable={
            docsScopeKey !== null
            && (entries ?? []).every((entry) => entry.kind !== 'document')
            && hasQuickOpenProviders()
          }
        />
      )}
    </QuickOpenContext.Provider>
  );
}
